import { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import HistoricalService from '@/domain/Historical/HistoricalService';
import { DeviceReadingResponse } from '@/domain/Historical/Historical';
import { HistoricalDataConfig } from '@/components/General/PlayerChart';
import { transformMultipleHistoricalData, transformMultipleHistoricalReadingTimestamps  } from '@/utils/InteractiveSimulation';
import { DateInput, Parameter } from '@/pages/InteractiveSimulation/types/replayInteractive';

const historicalService = new HistoricalService();

interface UseHistoricalDataFetcherParams {
	parameters: Parameter[];
	chartStartDate: DateInput;
	chartEndDate: DateInput;
}

interface UseHistoricalDataFetcherReturn {
	historicalData: HistoricalDataConfig[];
	isLoadingHistoricalData: boolean;
	fetchHistoricalData: () => Promise<void>;
	clearHistoricalData: () => void;
}

export const useHistoricalDataFetcher = ({
	parameters,
	chartStartDate,
	chartEndDate,
}: UseHistoricalDataFetcherParams): UseHistoricalDataFetcherReturn => {
	const [historicalData, setHistoricalData] = useState<HistoricalDataConfig[]>([]);
	const [isLoadingHistoricalData, setIsLoadingHistoricalData] = useState(false);

	const fetchHistoricalData = useCallback(async () => {
		if (parameters?.length === 0) {
			console.log('No parameters available to fetch data');
			return;
		}

		setIsLoadingHistoricalData(true);
		const startDateFormatted = dayjs(chartStartDate).format('YYYY-MM-DD HH:mm:ss');
		const endDateFormatted = dayjs(chartEndDate).format('YYYY-MM-DD HH:mm:ss');
		const rawHistoricalData: Record<string, DeviceReadingResponse | null | undefined> = {};

		for (const parameter of parameters) {
			try {
				const reading = await historicalService.getReadingsFromDeviceByParams(
					parameter.paremeterId.toString(),
					parameter.serialNumberSource,
					startDateFormatted,
					endDateFormatted,
					5,
					'minutes',
				);

				rawHistoricalData[parameter.paremeterId.toString()] = reading;
			} catch (error) {
				console.error(`Error ${parameter.paremeterId}:`, error);
				rawHistoricalData[parameter.paremeterId.toString()] = null;
			}
		}

		const transformedHistoricalData = transformMultipleHistoricalReadingTimestamps(rawHistoricalData);
		const chartFormattedData = transformMultipleHistoricalData(transformedHistoricalData, parameters);
		
		setHistoricalData(chartFormattedData);
		setIsLoadingHistoricalData(false);
	}, [parameters, chartStartDate, chartEndDate]);

	const clearHistoricalData = useCallback(() => {
		setHistoricalData([]);
	}, []);

	return {
		historicalData,
		isLoadingHistoricalData,
		fetchHistoricalData,
		clearHistoricalData,
	};
};
